/**
 * Step 6 · Classification & Economics
 */
import { Sparkle, ArrowClockwise } from "@phosphor-icons/react";
import { StepHeader } from "./Step1Identity";
import ConfigFieldRow from "../parts/ConfigFieldRow";

const NUMERIC = [
  { field: "cutoff_grade",           label: "Working Cut-off Grade",     unit: "g/t · %", step: 0.01, hint: "Final cut-off requires CP sign-off" },
  { field: "reporting_cutoff",       label: "Reporting Cut-off Grade",   unit: "g/t · %", step: 0.01 },
  { field: "measured_distance",     label: "Measured Distance",         unit: "m",        step: 1 },
  { field: "indicated_distance",     label: "Indicated Distance",         unit: "m",        step: 1 },
  { field: "inferred_distance",      label: "Inferred Distance",          unit: "m",        step: 1 },
  { field: "metal_price",            label: "Metal Price",                 unit: "$/oz · $/t", step: 1 },
  { field: "mining_cost",            label: "Mining Cost",                 unit: "$/t",     step: 0.1 },
  { field: "processing_cost",        label: "Processing Cost",             unit: "$/t",     step: 0.1 },
  { field: "g_and_a_cost",           label: "G&A Cost",                   unit: "$/t",     step: 0.1 },
  { field: "selling_cost",           label: "Selling Cost",                 unit: "$/t · %", step: 0.01 },
  { field: "mining_recovery",        label: "Mining Recovery",              unit: "%",       step: 0.1 },
  { field: "processing_recovery",    label: "Processing Recovery",          unit: "%",       step: 0.1 },
  { field: "dilution",               label: "Mining Dilution",              unit: "%",       step: 0.1 },
];

const METHODS = ["distance", "kriging_variance", "block_confidence", "manual"];
const MINING = ["open_pit", "underground", "combined", "in_situ_leach", "placer"];

export default function Step6Classification({ cfg, patchCfg, aiApply, aiApplying, draftId }) {
  const cls = cfg?.classification || {};

  const applyAI = () => draftId && aiApply({});

  return (
    <section className="space-y-4" data-testid="step-6-classification">
      <StepHeader step="Step 6" title="Classification & Economics"
        subtitle="Cut-off, prices, costs and recoveries. Final cut-off, price and classification method require Competent-Person sign-off before publication." />

      <div className="flex items-center justify-end">
        <button type="button" onClick={applyAI} disabled={aiApplying || !draftId}
                    data-testid="btn-apply-ai-suggestions-classification"
                    className="inline-flex items-center gap-1.5 rounded-md border border-brand-blue/30 bg-brand-blue/5 hover:bg-brand-blue/10 px-3 h-9 text-[12px] text-brand-blue font-medium disabled:opacity-50">
          {aiApplying ? <ArrowClockwise className="animate-spin" size={12} /> : <Sparkle size={12} weight="fill" />}
          Apply AI suggestions
        </button>
      </div>

      <div className="rounded-lg bg-white border border-slate-200 divide-y divide-slate-100">
        <ConfigFieldRow section="classification" field="classification_method"
                              label="Classification Method" type="select" options={METHODS}
                              slot={cls.classification_method || {}}
                              onChange={(v) => patchCfg({ "classification.classification_method": { value: v } })}
                              onResetToAI={(ai) => patchCfg({ "classification.classification_method": { value: ai } })} />
        <ConfigFieldRow section="classification" field="mining_method"
                              label="Mining Method" type="select" options={MINING}
                              slot={cls.mining_method || {}}
                              onChange={(v) => patchCfg({ "classification.mining_method": { value: v } })}
                              onResetToAI={(ai) => patchCfg({ "classification.mining_method": { value: ai } })} />
        <ConfigFieldRow section="classification" field="reporting_currency"
                              label="Reporting Currency" type="text"
                              slot={cls.reporting_currency || {}}
                              onChange={(v) => patchCfg({ "classification.reporting_currency": { value: v } })}
                              onResetToAI={(ai) => patchCfg({ "classification.reporting_currency": { value: ai } })} />
        {NUMERIC.map((f) => (
          <ConfigFieldRow key={f.field} section="classification" field={f.field}
                                label={f.label} unit={f.unit} type="number" step={f.step} hint={f.hint}
                                slot={cls[f.field] || {}}
                                onChange={(v) => patchCfg({ [`classification.${f.field}`]: { value: v } })}
                                onResetToAI={(ai) => patchCfg({ [`classification.${f.field}`]: { value: ai } })} />
        ))}
      </div>
    </section>
  );
}
